import { useState, useEffect, useRef, FormEvent } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import { useBilingual } from "@/lib/bilingual";
import { openMailto, formatBody } from "@/lib/mailto";

// Cart-free RFQ / contact form. Lives on /contact and is the single
// conversion endpoint for the site. The `?intent` query (quote | sample |
// visit) preselects the purpose; unknown / absent intent defaults to general.

const PURPOSES = ["quote", "sample", "visit", "general"] as const;
type Purpose = (typeof PURPOSES)[number];
const isPurpose = (v: string): v is Purpose =>
  (PURPOSES as readonly string[]).includes(v);

const PURPOSE_EN: Record<Purpose, string> = {
  quote: "Request a quotation",
  sample: "Request a sample",
  visit: "Schedule a factory visit",
  general: "General inquiry",
};

const ContactForm = () => {
  const b = useBilingual();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const rawIntent = searchParams.get("intent") || "";
  const sectionRef = useRef<HTMLElement>(null);

  const [purpose, setPurpose] = useState<Purpose>(
    isPurpose(rawIntent) ? rawIntent : "general",
  );
  const [formStatus, setFormStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [notes, setNotes] = useState("");
  const [formData, setFormData] = useState({
    companyName: "",
    contactPerson: "",
    email: "",
    phone: "",
    country: "",
    destinationPort: "",
  });

  // When an intent arrives via the URL (from any CTA on the site), preselect
  // the matching purpose and bring the form into view.
  // Re-run on every navigation (location.key), not just when the intent value
  // changes, so re-clicking a CTA that points at the current URL (e.g. the
  // closing "Start an Inquiry" while already on /contact?intent=quote) still
  // re-selects the purpose and scrolls the form into view.
  useEffect(() => {
    if (isPurpose(rawIntent)) {
      setPurpose(rawIntent);
      sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [rawIntent, location.key]);

  const purposeOptions: { value: Purpose; label: string; icon: string }[] = [
    { value: "quote", label: b("Request a quotation", "索取報價"), icon: "ri-send-plane-line" },
    { value: "sample", label: b("Request a sample", "索取樣品"), icon: "ri-flask-line" },
    { value: "visit", label: b("Schedule a factory visit", "安排參觀工廠"), icon: "ri-building-line" },
    { value: "general", label: b("General inquiry", "一般諮詢"), icon: "ri-chat-3-line" },
  ];

  const countries = [
    b("United States", "美國"),
    b("United Kingdom", "英國"),
    b("Germany", "德國"),
    b("France", "法國"),
    b("Italy", "義大利"),
    b("Spain", "西班牙"),
    b("UAE", "阿拉伯聯合大公國"),
    b("Saudi Arabia", "沙烏地阿拉伯"),
    b("Qatar", "卡達"),
    b("Kuwait", "科威特"),
    b("Oman", "阿曼"),
    b("Japan", "日本"),
    b("South Korea", "韓國"),
    b("Singapore", "新加坡"),
    b("Malaysia", "馬來西亞"),
    b("Canada", "加拿大"),
    b("Other", "其他"),
  ];

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (notes.length > 500) return;
    setFormStatus("sending");
    try {
      openMailto({
        subject: `[Forwaro Contact] ${PURPOSE_EN[purpose]} — ${formData.companyName || formData.contactPerson}`,
        body: formatBody([
          ["Purpose", PURPOSE_EN[purpose]],
          ["", ""],
          ["Company", formData.companyName],
          ["Contact", formData.contactPerson],
          ["Email", formData.email],
          ["Phone", formData.phone],
          ["Country", formData.country],
          ["Destination Port", formData.destinationPort],
          ["", ""],
          ["Message", `\n${notes}`],
        ]),
      });
      setFormStatus("success");
    } catch {
      setFormStatus("error");
    }
  };

  return (
    <section
      id="contact-form"
      ref={sectionRef}
      className="py-16 bg-stone-50 border-t border-stone-100 scroll-mt-24"
    >
      <div className="max-w-screen-xl mx-auto px-6 flex flex-col gap-8">
        <div>
          <p className="text-amber-700 text-xs font-semibold tracking-widest uppercase mb-2">
            {b("Contact Forwaro", "聯絡 Forwaro")}
          </p>
          <h2
            className="text-3xl md:text-4xl font-bold text-stone-900"
            style={{ fontFamily: "var(--font-display)" }}
          >
            {b("Tell us what you need", "告訴我們您的需求")}
          </h2>
          <p className="text-stone-500 text-sm mt-3 max-w-xl leading-relaxed">
            {b(
              "Forwaro's export team reviews every message personally and responds with a tailored quote, product specifications and full export documentation. No automated replies.",
              "Forwaro 外銷團隊親自審閱每一則訊息，回覆客製報價、產品規格與完整出口文件。不使用自動化回信。",
            )}
          </p>
        </div>

        {formStatus === "success" ? (
          <div
            role="status"
            aria-live="polite"
            className="bg-white border border-stone-200 rounded-2xl p-16 flex flex-col items-center text-center gap-5"
          >
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-50 border border-green-200">
              <i className="ri-check-line text-3xl text-green-600" />
            </div>
            <h3 className="text-2xl font-bold text-stone-900" style={{ fontFamily: "var(--font-display)" }}>
              {b("Request received", "已收到您的需求")}
            </h3>
            <p className="text-stone-500 text-sm max-w-sm leading-relaxed">
              {b(
                "Thank you. Forwaro's export team will review your message and respond within two business days.",
                "謝謝您。Forwaro 外銷團隊將於兩個工作日內審閱並回覆您的訊息。",
              )}
            </p>
            <button
              onClick={() => setFormStatus("idle")}
              className="mt-2 text-amber-700 text-sm font-semibold underline cursor-pointer"
            >
              {b("Send another message", "再送出一則訊息")}
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
            {/* Purpose */}
            <div className="p-8 border-b border-stone-100">
              <label className="text-xs text-stone-500 mb-3 block font-medium">
                {b("How can we help?", "我們能如何協助您？")}
              </label>
              <div className="flex flex-wrap gap-2">
                {purposeOptions.map((p) => (
                  <button
                    key={p.value}
                    type="button"
                    onClick={() => setPurpose(p.value)}
                    aria-pressed={purpose === p.value}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border transition-all duration-150 cursor-pointer whitespace-nowrap ${
                      purpose === p.value
                        ? "bg-amber-700 border-amber-700 text-white"
                        : "bg-white border-stone-200 text-stone-600 hover:border-amber-400 hover:text-amber-700"
                    }`}
                  >
                    <i className={p.icon} />
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-stone-100">
              {/* LEFT — Company Details */}
              <div className="p-8 flex flex-col gap-5">
                <div className="mb-2">
                  <p className="text-amber-700 text-xs font-semibold tracking-widest uppercase mb-1">
                    {b("Company Details", "公司資料")}
                  </p>
                  <p className="text-stone-400 text-xs">
                    {b("Tell us about your business", "告訴我們您的公司資訊")}
                  </p>
                </div>

                <div>
                  <label className="text-xs text-stone-500 mb-1.5 block font-medium">
                    {b("Company Name", "公司名稱")} *
                  </label>
                  <input
                    type="text"
                    name="companyName"
                    required
                    value={formData.companyName}
                    onChange={handleChange}
                    placeholder={b("Your company name", "請輸入公司名稱")}
                    className="w-full border border-stone-200 rounded-lg px-4 py-2.5 text-sm text-stone-800 placeholder-stone-300 focus:outline-none focus:border-amber-500 transition-colors"
                  />
                </div>

                <div>
                  <label className="text-xs text-stone-500 mb-1.5 block font-medium">
                    {b("Contact Person", "聯絡人")} *
                  </label>
                  <input
                    type="text"
                    name="contactPerson"
                    required
                    value={formData.contactPerson}
                    onChange={handleChange}
                    placeholder={b("Full name", "姓名全稱")}
                    className="w-full border border-stone-200 rounded-lg px-4 py-2.5 text-sm text-stone-800 placeholder-stone-300 focus:outline-none focus:border-amber-500 transition-colors"
                  />
                </div>

                <div>
                  <label className="text-xs text-stone-500 mb-1.5 block font-medium">
                    {b("Email", "電子郵件")} *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={b("you@company.com", "您的商務信箱")}
                    className="w-full border border-stone-200 rounded-lg px-4 py-2.5 text-sm text-stone-800 placeholder-stone-300 focus:outline-none focus:border-amber-500 transition-colors"
                  />
                </div>

                <div>
                  <label className="text-xs text-stone-500 mb-1.5 block font-medium">
                    {b("Phone", "電話")}
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder={b("Include country code", "請含國碼")}
                    className="w-full border border-stone-200 rounded-lg px-4 py-2.5 text-sm text-stone-800 placeholder-stone-300 focus:outline-none focus:border-amber-500 transition-colors"
                  />
                </div>

                <div>
                  <label className="text-xs text-stone-500 mb-1.5 block font-medium">
                    {b("Country", "國家")} *
                  </label>
                  <select
                    name="country"
                    required
                    value={formData.country}
                    onChange={handleChange}
                    className="w-full border border-stone-200 rounded-lg px-4 py-2.5 text-sm text-stone-800 focus:outline-none focus:border-amber-500 transition-colors cursor-pointer"
                  >
                    <option value="">{b("Select your country", "請選擇國家")}</option>
                    {countries.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-xs text-stone-500 mb-1.5 block font-medium">
                    {b("Destination Port", "目的港")}
                  </label>
                  <input
                    type="text"
                    name="destinationPort"
                    value={formData.destinationPort}
                    onChange={handleChange}
                    placeholder={b("e.g. Jebel Ali, Hamburg, Long Beach", "例如：Jebel Ali、漢堡、長堤港")}
                    className="w-full border border-stone-200 rounded-lg px-4 py-2.5 text-sm text-stone-800 placeholder-stone-300 focus:outline-none focus:border-amber-500 transition-colors"
                  />
                </div>
              </div>

              {/* RIGHT — Message */}
              <div className="p-8 flex flex-col gap-5">
                <div className="mb-2">
                  <p className="text-amber-700 text-xs font-semibold tracking-widest uppercase mb-1">
                    {b("Your Message", "您的訊息")}
                  </p>
                  <p className="text-stone-400 text-xs">
                    {b("Tell us what you're looking for, in your own words", "用您自己的話告訴我們需求")}
                  </p>
                </div>

                <div className="flex-1 flex flex-col">
                  <label className="text-xs text-stone-500 mb-1.5 block font-medium">
                    {b("Message", "訊息")}{" "}
                    <span className="text-stone-400 font-normal">({notes.length}/500)</span>
                  </label>
                  <textarea
                    name="additionalNotes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    maxLength={500}
                    rows={8}
                    placeholder={b(
                      "Tell us about the products you're interested in, your target market, or any questions. Our export team will follow up personally.",
                      "告訴我們您感興趣的產品、目標市場，或任何問題。我們的外銷團隊會親自跟進。",
                    )}
                    className="w-full flex-1 border border-stone-200 rounded-lg px-4 py-2.5 text-sm text-stone-800 placeholder-stone-300 focus:outline-none focus:border-amber-500 transition-colors resize-none"
                  />
                  {notes.length > 500 && (
                    <p className="text-red-500 text-xs mt-1">
                      {b("Message cannot exceed 500 characters.", "訊息不得超過 500 字。")}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="px-8 py-6 bg-stone-50 border-t border-stone-100 flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-stone-400 text-xs leading-relaxed max-w-sm">
                {b(
                  "Your information is kept confidential and used only to respond to your message.",
                  "您的資料將保密處理，僅用於回覆此訊息。",
                )}
              </p>
              {formStatus === "error" && (
                <p className="text-red-500 text-xs">
                  {b("Something went wrong. Please try again.", "發生錯誤，請再試一次。")}
                </p>
              )}
              <button
                type="submit"
                disabled={formStatus === "sending" || notes.length > 500}
                className="bg-amber-700 hover:bg-amber-600 disabled:opacity-60 text-white font-semibold px-10 py-3.5 rounded-full transition-colors text-sm whitespace-nowrap cursor-pointer"
              >
                {formStatus === "sending"
                  ? b("Sending…", "傳送中…")
                  : b("Send message", "送出訊息")}
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
};

export default ContactForm;
